import { HttpErrorResponse } from '@angular/common/http';
import { ErrorResponseDto } from '../models/api-types';
import { buildTransientHint } from './transient-http';
import { buildBusinessConflictMessage, classifyConflict } from './concurrency-conflict';
import { translate as t } from './i18n';

export function formatHttpError(error: HttpErrorResponse): string {
  const status = error.status;
  const body = error.error as ErrorResponseDto | string | null | undefined;
  const conflict = classifyConflict(error);
  let message = '';
  let validation = '';
  let requestId = extractRequestIdFromHttpError(error) || '';

  if (typeof body === 'string') {
    message = body;
  } else if (body && typeof body === 'object') {
    message = body.message ? String(body.message) : JSON.stringify(body);
    requestId = body.requestId || requestId;
    const validationDetails = extractValidationDetailStrings(body.details);
    if (validationDetails.length > 0) {
      // class-validator failures arrive with `message: "Validation failed"` and a string[]
      // in `details`. Replace the generic message with the concrete constraints so the
      // user sees what's wrong instead of a requestId-only error.
      const joined = validationDetails.join('; ');
      if (isGenericValidationFailedMessage(message)) {
        message = joined;
      } else {
        validation = t(
          'ui.core.http.error.text.validacion',
          { validation: joined },
          ` Validación: ${joined}`
        );
      }
    } else if (body.validationErrors) {
      validation = t(
        'ui.core.http.error.text.validacion',
        { validation: JSON.stringify(body.validationErrors) },
        ` Validación: ${JSON.stringify(body.validationErrors)}`
      );
    }
  } else if (error.message) {
    message = error.message;
  }

  if (!message) {
    message = t('ui.core.http.error.text.error_desconocido', undefined, 'Algo salió mal.');
  }

  const knownMessage = resolveKnownMessage(status, body, message);
  if (knownMessage) {
    message = knownMessage;
  }

  if (conflict.kind === 'business') {
    message = buildBusinessConflictMessage(conflict);
  }

  const statusHint = getStatusHint(status, conflict.kind);
  const requestSuffix = requestId ? ` [requestId: ${requestId}]` : '';
  const transientHint = buildTransientHint(error);

  return t(
    'ui.core.http.error.text.estado',
    {
      status,
      statusHint,
      message,
      validation,
      requestSuffix,
      transientHint,
    },
    `Estado ${status}${statusHint ? ` (${statusHint})` : ''}: ${message}${validation}${requestSuffix}${transientHint ? ` ${transientHint}` : ''}`
  );
}

export function extractRequestIdFromHttpError(error: HttpErrorResponse): string | null {
  const fromHeader = error.headers?.get('X-Request-Id');
  if (fromHeader) {
    return fromHeader;
  }
  const body = error.error as ErrorResponseDto | null | undefined;
  if (body && typeof body === 'object' && body.requestId) {
    return String(body.requestId);
  }
  return null;
}

export function formatUnknownError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    return formatHttpError(error);
  }
  if (error instanceof Error) {
    return error.message || t('ui.core.http.error.text.error_desconocido', undefined, 'Algo salió mal.');
  }
  if (typeof error === 'string') {
    return error;
  }
  return t('ui.core.http.error.text.error_desconocido', undefined, 'Algo salió mal.');
}

function getStatusHint(status: number, conflictKind: 'none' | 'version' | 'business'): string {
  switch (status) {
    case 403:
      return t('ui.core.http.error.text.acceso_denegado', undefined, 'Sin permiso');
    case 409:
      if (conflictKind === 'business') {
        return t('ui.core.http.error.text.conflicto_de_negocio', undefined, 'conflicto de negocio');
      }
      if (conflictKind === 'version') {
        return t('ui.core.http.error.text.conflicto_de_version', undefined, 'conflicto de version');
      }
      return t('ui.core.http.error.text.conflicto', undefined, 'conflicto');
    case 429:
      return t('ui.core.http.error.text.demasiadas_solicitudes', undefined, 'Espera un momento');
    case 503:
      return t('ui.core.http.error.text.servicio_no_disponible', undefined, 'Ahora no está disponible');
    case 504:
      return t('ui.core.http.error.text.tiempo_de_espera_agotado', undefined, 'Tardó demasiado');
    default:
      return '';
  }
}

function resolveKnownMessage(
  status: number,
  body: ErrorResponseDto | string | null | undefined,
  rawMessage: string
): string | null {
  const normalizedMessage = rawMessage.trim().toLowerCase();
  const structuredErrorCode = normalizeStructuredMarker(
    body && typeof body === 'object' ? body.errorCode : null
  );
  const structuredReason = normalizeStructuredMarker(extractDetailsReason(body));

  if (
    structuredErrorCode?.includes('STUDENT_LEVEL_REQUIRED') ||
    structuredReason?.includes('STUDENT_LEVEL_REQUIRED') ||
    normalizedMessage.includes('student level is required')
  ) {
    return t(
      'ui.core.http.error.text.el_nivel_es_obligatorio_cuando_el_rol_es_alumno',
      undefined,
      'Elige un nivel para Alumno.'
    );
  }

  if (
    structuredErrorCode?.includes('ONLY_STUDENT_USERS_CAN_HAVE_LEVEL') ||
    structuredReason?.includes('ONLY_STUDENT_USERS_CAN_HAVE_LEVEL') ||
    normalizedMessage.includes('only student users can have a level')
  ) {
    return t(
      'ui.core.http.error.text.solo_los_usuarios_alumno_pueden_tener_nivel',
      undefined,
      'Solo Alumno puede tener nivel.'
    );
  }

  /*
   * El 429 llegaba con el nombre de la excepcion de la libreria tal cual
   * —«ThrottlerException: Too Many Requests»— y eso es lo que leia la clienta,
   * en ingles y nombrando una clase de codigo. Lo que necesita saber es que no
   * ha roto nada y que se arregla solo.
   */
  if (status === 429) {
    return t(
      'ui.core.http.error.text.demasiadas_peticiones_seguidas',
      undefined,
      'Demasiadas peticiones seguidas. Espera unos segundos y vuelve a intentarlo.'
    );
  }

  if (
    status === 403 &&
    (
      structuredErrorCode?.includes('INSUFFICIENT_PERMISSIONS') ||
      structuredReason?.includes('INSUFFICIENT_PERMISSIONS') ||
      structuredErrorCode?.includes('PERMISSION_DENIED') ||
      structuredReason?.includes('PERMISSION_DENIED') ||
      normalizedMessage.includes('insufficient permissions')
    )
  ) {
    return t(
      'ui.core.http.error.text.no_tienes_permisos_suficientes_para_modificar_este_nivel',
      undefined,
      'No puedes cambiar este nivel.'
    );
  }

  return null;
}

function extractValidationDetailStrings(details: unknown): string[] {
  if (!Array.isArray(details)) {
    return [];
  }
  const strings = details.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
  return strings;
}

function isGenericValidationFailedMessage(message: string): boolean {
  return message.trim().toLowerCase() === 'validation failed';
}

function extractDetailsReason(body: ErrorResponseDto | string | null | undefined): unknown {
  if (!body || typeof body !== 'object') {
    return null;
  }
  if (!body.details || typeof body.details !== 'object') {
    return null;
  }
  return (body.details as Record<string, unknown>)['reason'];
}

function normalizeStructuredMarker(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  return value
    .trim()
    .replace(/[\s\-]+/g, '_')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toUpperCase();
}
